/**
 * PipelineModal — editar o funil (nome, descrição, tipo) e EXCLUIR o funil.
 *
 * Aberto pela opção "Editar pipeline" do menu de ações (kebab) do board ou
 * pelo modal "Gerenciar funis".
 *
 * Exclusão (regra de negócio no backend, DELETE /crm/pipelines/{id}):
 *   - só admin/editor exclui; funil padrão e o único funil não podem ser excluídos;
 *   - as etapas são removidas (soft-delete) e os negócios ficam sem funil
 *     (pipeline_id/stage_id → NULL) — nada é apagado, sem órfãos.
 * A confirmação passa por um modal próprio deixando as consequências explícitas.
 *
 * Visual: mesmo padrão de modal centrado do módulo Financeiro (fc-modal-*,
 * ../financeiro/contas.css) + zona de exclusão (pm-danger-*, ./pipelines.css).
 *
 * Props:
 *   pipeline   — { id, nome, descricao, tipo, is_default }
 *   canDelete  — bool; se false, o botão excluir some e mostra o motivo
 *   deleteBlockReason — texto do motivo quando canDelete=false
 *   onClose()  — fecha sem mudar
 *   onSaved(p) — salvou a edição (recebe o pipeline atualizado)
 *   onDeleted()— excluiu (o board deve recarregar a lista de funis)
 */

import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { updatePipeline, deletePipeline } from '../../db/api.js'
import '../financeiro/contas.css'
import './pipelines.css'

const TIPOS = [
  { v: 'comercial', label: 'Comercial' },
  { v: 'pre_venda', label: 'Pré-venda' },
  { v: 'pos_venda', label: 'Pós-venda' },
]

export default function PipelineModal({
  pipeline, canDelete, deleteBlockReason, onClose, onSaved, onDeleted, tabs = null,
}) {
  const [nome, setNome] = useState(pipeline.nome || '')
  const [descricao, setDescricao] = useState(pipeline.descricao || '')
  const [tipo, setTipo] = useState(pipeline.tipo || 'comercial')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [confirming, setConfirming] = useState(false)   // modal de confirmação de exclusão
  const [deleting, setDeleting] = useState(false)

  const handleSave = async () => {
    if (saving) return
    if (!nome.trim()) { setErr('Nome é obrigatório.'); return }
    setSaving(true); setErr(null)
    try {
      const updated = await updatePipeline(pipeline.id, {
        nome: nome.trim(), descricao: descricao.trim() || null, tipo,
      })
      onSaved(updated)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true); setErr(null)
    try {
      await deletePipeline(pipeline.id)
      onDeleted()
    } catch (e) {
      setErr(e.message); setDeleting(false); setConfirming(false)
    }
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: tabs ? 720 : 500, maxWidth: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">{tabs ? 'Configurações do pipeline' : 'Editar pipeline'}</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>
        {tabs}
        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Nome<i>*</i></label>
            <input className="fc-input" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
          </div>

          <div className="fc-field">
            <label className="fc-label">Descrição</label>
            <textarea className="fc-input" style={{ height: 'auto', minHeight: 68, padding: '10px 12px', resize: 'vertical' }}
              value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>

          <div className="fc-field">
            <label className="fc-label">Tipo</label>
            <select className="fc-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>

          {/* Zona de exclusão */}
          <div className="pm-danger-zone">
            <div className="pm-danger-title">Excluir pipeline</div>
            {canDelete ? (
              <>
                <p className="pm-danger-text">
                  Remove este funil e todas as suas etapas. Os negócios vinculados
                  são preservados, mas deixam de pertencer a este funil.
                </p>
                <button className="pm-btn-danger solid" onClick={() => setConfirming(true)}>
                  Excluir pipeline
                </button>
              </>
            ) : (
              <p className="pm-danger-text">{deleteBlockReason || 'Este funil não pode ser excluído.'}</p>
            )}
          </div>
        </div>

        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onClose}>Cancelar</button>
          <button className="fc-ctrl primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Modal de confirmação da exclusão */}
      {confirming && (
        <div className="fc-modal-overlay" onMouseDown={() => !deleting && setConfirming(false)}>
          <div className="fc-modal" style={{ width: 440 }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="fc-modal-head">
              <span className="fc-drawer-title">Excluir "{pipeline.nome}"?</span>
            </div>
            <div className="fc-modal-body">
              <p style={{ margin: '0 0 8px', fontSize: 13.5, color: '#344054' }}>
                Esta ação não pode ser desfeita. Ao excluir este funil:
              </p>
              <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13, color: '#667085', lineHeight: 1.6 }}>
                <li>todas as <b>etapas</b> do funil serão removidas;</li>
                <li>todos os <b>negócios vinculados</b> deixarão de pertencer a este funil
                    (ficam preservados, sem funil e sem etapa).</li>
              </ul>
              {err && <div className="fc-error">{err}</div>}
            </div>
            <div className="fc-modal-foot">
              <button className="fc-ctrl" onClick={() => setConfirming(false)} disabled={deleting}>
                Cancelar
              </button>
              <button className="pm-btn-danger solid" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Excluindo…' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
